import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { GET_BLOG_ARTICLES_BY_ID } from './query';

/* ── Back link ──────────────────────────────────────────────── */

const BackLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-family: var(--font-primary), sans-serif;
  font-weight: 400;
  font-size: 1.2rem;
  color: #888;
  text-decoration: none;
  padding: 1.6rem 2rem;

  &::before {
    content: '←';
  }

  &:hover {
    color: #1a1a1a;
  }
`;

/* ── Article header ─────────────────────────────────────────── */

const Header = styled.div`
  display: grid;
  grid-template-columns: 55% 1fr;
  gap: 4rem;
  align-items: center;
  padding: 4rem 0;
  /* border-bottom: 1px solid #e0e0e0; */

  @media screen and (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
  /* aspect-ratio: 390 / 698; */
`;

const ArticleImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const HeroInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const Categories = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-bottom: 1.2rem;
`;

const Category = styled.span`
  font-family: var(--font-primary), sans-serif;
  font-weight: 500;
  font-size: 1.1rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--design-base-color);
`;

const Title = styled.h1`
  font-family: var(--font-primary), sans-serif;
  font-weight: 600;
  font-size: 3.8rem;
  color: var(--design-text-color);
  line-height: 1.2;
  margin-bottom: 2rem;

  @media screen and (max-width: 900px) {
    font-size: 3rem;
  }
`;

const DateLine = styled.p`
  font-family: var(--font-primary), sans-serif;
  font-weight: 400;
  font-size: 1.2rem;
  color: #aaa;
  margin: 0;
`;

/* ── Body content ───────────────────────────────────────────── */

const Body = styled.div`
  max-width: 100%;
  margin: 0 0;
  padding: 4rem 0rem;
`;

const Content = styled.div`
  font-family: var(--font-primary), sans-serif;
  font-weight: 400 !important;
  font-size: 2rem;
  line-height: 1.9;
  color: var(--design-text-color);

  p {
    font-size: 1.6rem;
    line-height: 1.7;
    margin: 0 0 1.6rem;
    color: var(--design-text-color);
  }

  h2 {
    font-family: var(--font-primary), sans-serif;
    font-weight: 500 !important;
    font-size: 1.6rem;
    /* letter-spacing: 0.14em; */
    text-transform: uppercase;
    color: var(--design-text-color);
    margin: 5rem 0 2rem;
    /* text-align: center; */
  }

  a {
    color: var(--design-base-color);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
  strong {
    font-weight: bold !important;
  }

  em, i {
    font-style: italic;
  }

  u {
    text-decoration: underline;
  }

  ul {
    list-style-type: disc;
    padding-left: 2.4rem;
    margin: 0 0 1.6rem;

    li {
      font-size: 1.6rem;
      line-height: 1.7;
      margin-bottom: 0.8rem;
      color: var(--design-text-color);
    }
  }

  ol {
    list-style-type: decimal;
    padding-left: 2.4rem;
    margin: 0 0 1.6rem;

    li {
      font-size: 1.6rem;
      line-height: 1.7;
      margin-bottom: 0.8rem;
      color: var(--design-text-color);
    }
  }
`;

const getArticleIdFromUrl = () => {
  // URL pattern: /magazine/article/{slug}/{id}/
  const match = window.location.pathname.match(
    /\/magazine\/article\/[^/]+\/(\d+)/
  );
  return match?.[1] ?? null;
};

const params = {
  dimensions: {
    width: 698,
    height: 390,
  },
};

const paramsBannerUrl = JSON.stringify(params);

const withImageParams = (url: string) => `${url}?params=${paramsBannerUrl}`;

export const MagazineArticle = () => {
  const { t, i18n } = useTranslation('magazine', { useSuspense: false });

  const id = getArticleIdFromUrl();
  // const id = 1;

  const { data } = useQuery(GET_BLOG_ARTICLES_BY_ID, {
    variables: { id },
    skip: !id,
  });
  console.log({ data });

  const article = data?.blogArticleById;

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat(i18n.language, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  };

  return (
    <article>
      <BackLink href="/magazine/index/">{t('backToMagazine')}</BackLink>

      {article && (
        <>
          <Header>
            <ImageWrapper>
              <ArticleImage
                src={withImageParams(article.image)}
                alt={article.title}
              />
            </ImageWrapper>
            <HeroInfo>
              {article.theme?.length > 0 && (
                <Categories>
                  {article.theme.map((theme: { name: string }) => (
                    <Category className="font-medium" key={theme.name}>
                      {theme.name}
                    </Category>
                  ))}
                </Categories>
              )}
              <Title className="font-semiBold">{article.title}</Title>
              <DateLine className="font-regular">
                {formatDate(article.publicationDate)}
              </DateLine>
            </HeroInfo>
          </Header>

          <Body>
            <Content dangerouslySetInnerHTML={{ __html: article.text }} />
          </Body>
        </>
      )}
    </article>
  );
};

export default MagazineArticle;
